import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ProtocolResponse, protocolsService } from "../../services/protocolsService";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

interface ProtocolHeaderProps {
  protocolData: ProtocolResponse;
}

const ProtocolHeader: React.FC<ProtocolHeaderProps> = ({ protocolData }) => {
  const { t } = useTranslation();
  const [isUpdating, setIsUpdating] = useState(false);
  const [currentExamDate, setCurrentExamDate] = useState<Date | null>(
    protocolData.examDate ? new Date(protocolData.examDate) : null
  );
  const {
    protocolId,
    protocolNumber,
    teacherName,
    subjectName,
    specialityName,
    examDate,
    academicYearName,
    semesterTypeName,
    examTypeName,
    sessionTypeName,
    isSigned,
    protocolTeachers,
  } = protocolData;

  const handleExamDateChange = async (date: Date | null) => {
    if (!date || isUpdating || isSigned) return;

    try {
      setIsUpdating(true);
      const dateWithoutTime = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      await protocolsService.updateProtocolExamDate(protocolId, dateWithoutTime.toISOString());
      setCurrentExamDate(dateWithoutTime);
    } catch (error) {
      console.error('Failed to update exam date:', error);
      alert(t('Failed to update exam date'));
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="bg-gray-100 p-6 rounded-lg shadow-lg mb-6 dark:bg-gray-800">
      <h1 className="text-2xl font-bold text-center dark:text-white">
        {t("TRAKIA UNIVERSITY – STARA ZAGORA")}
      </h1>
      <h2 className="text-xl font-semibold text-center mt-2 dark:text-white">
        {t("Protocol")} № {protocolNumber}
      </h2>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="protocol-info-item">
          <span className="protocol-info-item-label font-semibold mr-2">
            {t("Discipline")}:
          </span>
          <span className="protocol-info-item-value">{subjectName}</span>
        </div>
        <div className="protocol-info-item">
          <span className="protocol-info-item-label font-semibold mr-2">
            {t("Speciality")}:
          </span>
          <span className="protocol-info-item-value">{specialityName}</span>
        </div>
        {protocolTeachers && protocolTeachers.length > 0 ? (
          <div className="protocol-info-item col-span-1 sm:col-span-2 lg:col-span-3">
            <span className="protocol-info-item-label font-semibold mr-2">
              {t("Lecturers")}:
            </span>
            <span className="protocol-info-item-value">
              {teacherName}<br />
              {protocolTeachers.map((teacher, idx) => (
                <div key={teacher.teacherId}>
                  {teacher.teacherFullName || teacher.teacherName}
                </div>
              ))}
            </span>
          </div>
        ) : (
          <div className="protocol-info-item">
            <span className="protocol-info-item-label font-semibold mr-2">
              {t("Teacher")}:
            </span>
            <span className="protocol-info-item-value">{teacherName}</span>
          </div>
        )}
        <div className="protocol-info-item">
          <span className="protocol-info-item-label font-semibold mr-2">
            {t("Semester")}:
          </span>
          <span className="protocol-info-item-value">{semesterTypeName}</span>
        </div>
        {examTypeName && (
          <div className="protocol-info-item">
            <span className="protocol-info-item-label font-semibold mr-2">
              {t("Exam Type")}:
            </span>
            <span className="protocol-info-item-value">{examTypeName}</span>
          </div>
        )}
        {sessionTypeName && (
          <div className="protocol-info-item">
            <span className="protocol-info-item-label font-semibold mr-2">
              {t("Session Type")}:
            </span>
            <span className="protocol-info-item-value">
              {sessionTypeName}
            </span>
          </div>
        )}
        <div className="protocol-info-item">
          <span className="protocol-info-item-label font-semibold mr-2">
            {t("Academic Year")}:
          </span>
          <span className="protocol-info-item-value">{academicYearName}</span>
        </div>
        <div className="protocol-info-item">
          <span className="protocol-info-item-label font-semibold mr-2">
            {t("Exam Date")}:
          </span>
          <span className="protocol-info-item-value">
            {!isSigned ? (
              <DatePicker
                selected={currentExamDate}
                onChange={handleExamDateChange}
                dateFormat="dd.MM.yyyy"
                className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1"
                disabled={isUpdating || isSigned}
                showYearDropdown
                showMonthDropdown
              />
            ) : (
              examDate && new Date(examDate).toLocaleDateString()
            )}
          </span>
        </div>
        <div className="protocol-info-item">
          <span className="protocol-info-item-label font-semibold mr-2">
            {t("Signed")}:
          </span>
          <span className="protocol-info-item-value">
            {isSigned ? t("Yes") : t("No")}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProtocolHeader; 